/**
 * API Service for fetching project-related data
 * 
 * This module provides functions for fetching project data, metrics, and insights.
 */

import { 
  Project, 
  Task, 
  ProjectMetrics, 
  InsightsResponse,
  Calendar,
  WBSNode,
  Resource
} from '../components/ui/types';
import { API_BASE_URL } from '../config';
import { getAuthHeaders } from './workosAuth';

// Re-export imported types
export type { Project, Task, ProjectMetrics, InsightsResponse, Calendar, WBSNode, Resource };

/**
 * Fetch a list of all projects
 */
export async function getProjects(): Promise<Project[]> {
  try {
    const headers = await getAuthHeaders();
    const xerResponse = await fetch(`${API_BASE_URL}/xer/dashboard`, { credentials: 'include', headers });
    if (xerResponse.ok) {
      const xerData = await xerResponse.json();
      if (xerData.status === 'success' && xerData.data?.projects) {
        // Convert XER projects to Project format
        return xerData.data.projects.map((project: any) => ({
          id: project.id.toString(),
          name: project.name,
          description: `XER Project: ${project.file_name}`,
          status: 'active',
          progress: 0,
          start_date: project.created_at,
          end_date: project.created_at,
          organization: 'Edge Technologies',
          created_by: 'System',
          created_at: project.created_at,
          updated_at: project.created_at,
          task_count: project.task_count || 0,
          resource_count: project.resource_count || 0,
          relationship_count: project.relationship_count || 0
        }));
      }
    }
    
    // Fallback to standard projects endpoint
    const response = await fetch(`${API_BASE_URL}/projects`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch projects: ${response.statusText}`);
    }
    const data = await response.json();
    
    // Extract the projects array from the data field of the paginated response
    const projectsData = data.data || [];
    
    // Map API response to match our Project interface
    return projectsData.map((project: any) => ({
      ...project,
      // Handle discrepancy between finish_date and end_date
      end_date: project.end_date || project.finish_date,
      // Ensure required fields are present
      status: project.status || 'active',
      progress: project.progress || project.percent_complete || 0
    }));
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

/**
 * Fetch a single project by ID
 */
export async function getProject(projectId: string): Promise<Project | null> {
  try {
    const headers = await getAuthHeaders();
    // First try to get XER project details
    const xerResponse = await fetch(`${API_BASE_URL}/xer/dashboard`, { credentials: 'include', headers });
    if (xerResponse.ok) {
      const xerData = await xerResponse.json();
      if (xerData.status === 'success' && xerData.data?.projects) {
        const xerProject = xerData.data.projects.find((p: any) => p.id.toString() === projectId);
        if (xerProject) {
          return {
            id: xerProject.id.toString(),
            name: xerProject.name,
            description: `XER Project: ${xerProject.file_name}`,
            status: 'active',
            progress: 0,
            start_date: xerProject.created_at,
            end_date: xerProject.created_at,
            organization: 'Edge Technologies',
            created_by: 'System',
            created_at: xerProject.created_at,
            updated_at: xerProject.created_at,
            task_count: xerProject.task_count || 0,
            resource_count: xerProject.resource_count || 0,
            relationship_count: xerProject.relationship_count || 0
          };
        }
      }
    }
    
    // Fallback to standard project endpoint
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch project: ${response.statusText}`);
    }
    const data = await response.json();
    
    // Return the project data from the data field of the response
    const projectData = data.data || null;
    
    if (!projectData) return null;
    
    // Map API response to match our Project interface
    return {
      ...projectData,
      // Handle discrepancy between finish_date and end_date
      end_date: projectData.end_date || projectData.finish_date,
      // Ensure required fields are present
      status: projectData.status || 'active',
      progress: projectData.progress || projectData.percent_complete || 0
    };
  } catch (error) {
    console.error(`Error fetching project ${projectId}:`, error);
    return null;
  }
}

/**
 * Fetch project tasks
 */
export async function getProjectTasks(projectId: string): Promise<Task[]> {
  try {
    const headers = await getAuthHeaders();
    const xerResponse = await fetch(`${API_BASE_URL}/xer/dashboard`, { credentials: 'include', headers });
    if (xerResponse.ok) {
      const xerData = await xerResponse.json();
      if (xerData.status === 'success' && xerData.data?.recent_tasks) {
        // Convert XER tasks to Task format
        return xerData.data.recent_tasks.map((task: any) => ({
          id: task.task_id || 'unknown',
          name: task.task_name || 'Unknown Task',
          description: '',
          status: 'active',
          progress: 0,
          start_date: new Date().toISOString(),
          end_date: new Date().toISOString(),
          duration: task.duration || 0,
          project_id: projectId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
      }
    }
    
    // Fallback to standard endpoint
    const response = await fetch(`${API_BASE_URL}/xer/${projectId}/activities`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch tasks: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching tasks for project ${projectId}:`, error);
    return [];
  }
}

/**
 * Fetch project summary information
 */
export async function getProjectSummary(projectId: string): Promise<any> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/xer/${projectId}/summary`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch project summary: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching summary for project ${projectId}:`, error);
    return null;
  }
}

/**
 * Fetch project metrics for dashboard display
 */
export async function getProjectMetrics(projectId: string): Promise<ProjectMetrics | null> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/ai/project-metrics?project_id=${projectId}`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch project metrics: ${response.statusText}`);
    }
    const data = await response.json();
    return data.metrics as ProjectMetrics;
  } catch (error) {
    console.error(`Error fetching metrics for project ${projectId}:`, error);
    return null;
  }
}

/**
 * Fetch AI insights for a project
 */
export async function getAIInsights(projectId: string): Promise<InsightsResponse | null> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/ai/insights?project_id=${projectId}`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch AI insights: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching insights for project ${projectId}:`, error);
    return null;
  }
}

/**
 * Fetch optimized scenarios for a project
 */
export async function getOptimizedScenarios(projectId: string, options = {}): Promise<any> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/batch-optimization/generate-optimized-schedule`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_id: projectId,
        ...options
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch optimized scenarios: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error fetching optimized scenarios for project ${projectId}:`, error);
    return { scenarios: [] };
  }
}

/**
 * Fetch report templates
 */
export async function getReportTemplates(): Promise<any[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/reports/templates`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch report templates: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching report templates:', error);
    return [];
  }
}

/**
 * Generate a report for a project
 */
export async function generateReport(projectId: string, templateId: string, options = {}): Promise<any> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/reports/generate`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_id: projectId,
        template_id: templateId,
        ...options
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to generate report: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error generating report for project ${projectId}:`, error);
    return null;
  }
}

/**
 * Fetch project calendars
 */
export async function getProjectCalendars(projectId: string): Promise<Calendar[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/xer/project/${projectId}`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch calendars: ${response.statusText}`);
    }
    const data = await response.json();
    // Extract calendar data from project details
    if (data.data && data.data.entities && data.data.entities.calendars) {
      return data.data.entities.calendars;
    }
    return [];
  } catch (error) {
    console.error(`Error fetching calendars for project ${projectId}:`, error);
    return [];
  }
}

/**
 * Fetch project WBS structure
 */
export async function getProjectWBS(projectId: string): Promise<WBSNode[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/xer/${projectId}/wbs`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch WBS: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching WBS for project ${projectId}:`, error);
    return [];
  }
}

/**
 * Fetch project resources
 */
export async function getProjectResources(projectId: string): Promise<Resource[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/xer/${projectId}/resources`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch resources: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching resources for project ${projectId}:`, error);
    return [];
  }
}

/**
 * Fetch task financial data
 */
export async function getTaskFinancials(projectId: string): Promise<any[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/xer/${projectId}/task-financials`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch task financials: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching task financials for project ${projectId}:`, error);
    return [];
  }
}

/**
 * Fetch financial periods
 */
export async function getFinancialPeriods(projectId: string): Promise<any[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/xer/${projectId}/financial-periods`, { credentials: 'include', headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch financial periods: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching financial periods for project ${projectId}:`, error);
    return [];
  }
}

/**
 * Fetch detailed information for a specific list of activities by IDs
 */
export async function getActivitiesById(projectId: string, activityIds: string[]): Promise<any[]> {
  try {
    // If no activity IDs, return empty array
    if (!activityIds || activityIds.length === 0) {
      console.log('No activity IDs provided to getActivitiesById');
      return [];
    }
    
    console.log(`getActivitiesById: Fetching activities for project ${projectId}`, activityIds);
    
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/xer/${projectId}/activities`, { credentials: 'include', headers });
    
    if (!response.ok) {
      console.error(`Error fetching activities: ${response.status} - ${response.statusText}`);
      throw new Error(`Failed to fetch activities: ${response.statusText}`);
    }
    
    const allActivities = await response.json();
    console.log(`getActivitiesById: Found ${allActivities.length} total activities`);
    
    // Step 1: Try exact matches first
    let requestedActivities = allActivities.filter((activity: any) => 
      activityIds.includes(activity.id)
    );
    
    // Step 2: If no exact matches found, try case-insensitive matching
    if (requestedActivities.length === 0) {
      console.log('No exact matches found, trying case-insensitive matching');
      const lowerCaseIds = activityIds.map(id => id.toLowerCase());
      requestedActivities = allActivities.filter((activity: any) => 
        activity.id && lowerCaseIds.includes(activity.id.toLowerCase())
      );
    }
    
    // Step 3: Try matching with whitespace trimming
    if (requestedActivities.length === 0) {
      console.log('No case-insensitive matches found, trying with trimmed whitespace');
      const trimmedIds = activityIds.map(id => id.trim());
      requestedActivities = allActivities.filter((activity: any) => 
        activity.id && trimmedIds.includes(activity.id.trim())
      );
    }
    
    console.log(`getActivitiesById: Matched ${requestedActivities.length} activities out of ${activityIds.length} requested IDs`);
    
    if (requestedActivities.length === 0 && allActivities.length > 0) {
      // Log sample of activity IDs for debugging
      console.log('Sample of activity IDs in the system:', allActivities.slice(0, 5).map((a: any) => a.id));
      console.log('Requested activity IDs that were not found:', activityIds);
    }
    
    return requestedActivities;
  } catch (error) {
    console.error(`Error fetching activities for project ${projectId}:`, error);
    return [];
  }
} 